import React, {useCallback, useState, useMemo} from 'react';
import {View, FlatList, ScrollView, StyleSheet, RefreshControl, TouchableOpacity, Image} from 'react-native';
import {
  Text, Chip, FAB, ActivityIndicator, Searchbar, Snackbar,
  IconButton, Menu, Dialog, Portal, Button,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, useFocusEffect} from '@react-navigation/native';
import {workerApi} from '../../services/api/workerApi';
import {workerTypeApi} from '../../services/api/workerTypeApi';
import {colors, spacing} from '../../theme';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';

const EMPLOYMENT_TYPES = [
  'FULL_TIME', 'PART_TIME', 'CONTRACT', 'FREELANCER', 'DAILY_WAGE',
  'PIECE_RATE', 'COMMISSION_BASED', 'OUTSOURCED', 'TEMPORARY', 'INTERN',
];

const EMPLOYMENT_LABELS = {
  FULL_TIME: 'Full Time',
  PART_TIME: 'Part Time',
  CONTRACT: 'Contract',
  FREELANCER: 'Freelancer',
  DAILY_WAGE: 'Daily Wage',
  PIECE_RATE: 'Piece Rate',
  COMMISSION_BASED: 'Commission',
  OUTSOURCED: 'Outsourced',
  TEMPORARY: 'Temporary',
  INTERN: 'Intern',
};

const WorkerListScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const [workers, setWorkers] = useState([]);
  const [workerTypes, setWorkerTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [snackbar, setSnackbar] = useState('');

  const [filterVisible, setFilterVisible] = useState(false);
  const [filterWorkerTypeId, setFilterWorkerTypeId] = useState(null);
  const [filterEmploymentType, setFilterEmploymentType] = useState(null);
  const [filterStatus, setFilterStatus] = useState(true);
  const [filterHasUser, setFilterHasUser] = useState(null);

  const [actionWorker, setActionWorker] = useState(null);
  const [deactivateVisible, setDeactivateVisible] = useState(false);

  const hasFilters = filterWorkerTypeId || filterEmploymentType || filterStatus !== null || filterHasUser !== null;

  const fetchWorkerTypes = useCallback(async () => {
    try {
      const res = await workerTypeApi.listWorkerTypes();
      setWorkerTypes(res.data.data || []);
    } catch (e) {}
  }, []);

  const fetchWorkers = useCallback(async (query) => {
    try {
      const params = {page: 0, size: 100};
      if (query) params.q = query;
      if (filterWorkerTypeId) params.workerTypeId = filterWorkerTypeId;
      if (filterEmploymentType) params.employmentType = filterEmploymentType;
      if (filterStatus !== null) params.isActive = filterStatus;
      if (filterHasUser !== null) params.hasUser = filterHasUser;
      const res = await workerApi.search(params);
      setWorkers(res.data.data.content || []);
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [filterWorkerTypeId, filterEmploymentType, filterStatus, filterHasUser, t]);

  useFocusEffect(
    useCallback(() => {
      setLoading(true);
      Promise.all([fetchWorkers(searchQuery), fetchWorkerTypes()])
        .finally(() => setLoading(false));
    }, [fetchWorkers, fetchWorkerTypes, searchQuery])
  );

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchWorkers(searchQuery);
    setRefreshing(false);
  }, [fetchWorkers, searchQuery]);

  const handleSearch = useCallback((query) => setSearchQuery(query), []);

  const handleSearchSubmit = useCallback(() => {
    setLoading(true);
    fetchWorkers(searchQuery).finally(() => setLoading(false));
  }, [fetchWorkers, searchQuery]);

  const clearFilters = useCallback(() => {
    setFilterWorkerTypeId(null);
    setFilterEmploymentType(null);
    setFilterStatus(null);
    setFilterHasUser(null);
    setFilterVisible(false);
  }, []);

  const handleDeactivate = useCallback(async () => {
    if (!actionWorker) return;
    setDeactivateVisible(false);
    try {
      await workerApi.delete(actionWorker.id);
      await fetchWorkers(searchQuery);
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
    setActionWorker(null);
  }, [actionWorker, fetchWorkers, searchQuery, t]);

  const handleReactivate = useCallback(async (worker) => {
    try {
      await workerApi.reactivate(worker.id);
      await fetchWorkers(searchQuery);
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [fetchWorkers, searchQuery, t]);

  const workerTypeMap = useMemo(() => {
    const map = {};
    workerTypes.forEach(wt => { map[wt.id] = wt.displayNameOverride || wt.name; });
    return map;
  }, [workerTypes]);

  const renderWorker = useCallback(({item}) => {
    const isFemale = item.gender === 'FEMALE';
    const accentColor = isFemale ? '#E91E63' : colors.primary;

    return (
      <TouchableOpacity
        activeOpacity={0.7}
        onPress={() => navigation.navigate('WorkerDetail', {workerId: item.id})}>
        <View style={[styles.card, !item.isActive && styles.cardInactive]}>
          <View style={[styles.accentBar, {backgroundColor: item.isActive ? accentColor : colors.textLight}]} />
          <View style={styles.cardBody}>
            {/* Avatar */}
            {item.avatarUrl ? (
              <Image source={{uri: item.avatarUrl}} style={styles.avatarImage} />
            ) : (
              <View style={[styles.avatar, {backgroundColor: accentColor + '15'}]}>
                <Text style={[styles.avatarText, {color: accentColor}]}>
                  {(item.fullName || '?').charAt(0).toUpperCase()}
                </Text>
              </View>
            )}

            {/* Info */}
            <View style={styles.cardInfo}>
              <View style={styles.nameRow}>
                <Text style={styles.workerName} numberOfLines={1}>{item.fullName}</Text>
                {item.gender && (
                  <Icon
                    name={isFemale ? 'gender-female' : 'gender-male'}
                    size={14}
                    color={isFemale ? '#E91E63' : colors.info}
                  />
                )}
                <View style={[styles.statusDot, {backgroundColor: item.isActive ? colors.success : colors.textLight}]} />
              </View>

              {item.phone ? <Text style={styles.phone}>{item.phone}</Text> : null}

              <View style={styles.metaRow}>
                {item.workerTypeName ? (
                  <View style={styles.metaBadge}>
                    <Text style={styles.metaBadgeText}>{item.workerTypeName}</Text>
                  </View>
                ) : null}
                {item.employmentType ? (
                  <View style={[styles.metaBadge, {backgroundColor: colors.secondary + '12'}]}>
                    <Text style={[styles.metaBadgeText, {color: colors.secondary}]}>
                      {EMPLOYMENT_LABELS[item.employmentType] || item.employmentType}
                    </Text>
                  </View>
                ) : null}
                {item.workerCode ? (
                  <Text style={styles.workerCode}>{item.workerCode}</Text>
                ) : null}
              </View>

              {item.primaryOrgUnitName ? (
                <View style={styles.orgRow}>
                  <Icon name="store" size={12} color={colors.textLight} />
                  <Text style={styles.orgText}>{item.primaryOrgUnitName}</Text>
                </View>
              ) : null}
            </View>

            {/* Actions */}
            <View style={styles.cardActions}>
              <TouchableOpacity
                style={styles.actionCircle}
                onPress={() => navigation.navigate('WorkerForm', {workerId: item.id})}>
                <Icon name="pencil-outline" size={16} color={colors.primary} />
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.actionCircle, {backgroundColor: colors.secondary + '12'}]}
                onPress={() => navigation.navigate('WorkerRateSetup', {worker: item})}>
                <Icon name="currency-inr" size={16} color={colors.secondary} />
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </TouchableOpacity>
    );
  }, [navigation]);

  if (loading && workers.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.searchRow}>
        <Searchbar
          placeholder={t('worker.searchPlaceholder')}
          value={searchQuery}
          onChangeText={handleSearch}
          onSubmitEditing={handleSearchSubmit}
          style={styles.searchbar}
          inputStyle={styles.searchInput}
        />
        <IconButton
          icon={hasFilters ? 'filter-check' : 'filter-variant'}
          size={24}
          iconColor={hasFilters ? colors.primary : colors.textSecondary}
          onPress={() => setFilterVisible(true)}
        />
      </View>

      {hasFilters && (
        <View style={styles.activeFilters}>
          {filterWorkerTypeId && (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterWorkerTypeId(null)}>
              {workerTypeMap[filterWorkerTypeId] || 'Type'}
            </Chip>
          )}
          {filterEmploymentType && (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterEmploymentType(null)}>
              {EMPLOYMENT_LABELS[filterEmploymentType]}
            </Chip>
          )}
          {filterStatus !== null && (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterStatus(null)}>
              {filterStatus ? t('worker.active') : t('worker.inactive')}
            </Chip>
          )}
          {filterHasUser !== null && (
            <Chip style={styles.filterChip} textStyle={styles.filterChipText}
              onClose={() => setFilterHasUser(null)}>
              {filterHasUser ? t('worker.hasLogin') : t('worker.noLogin')}
            </Chip>
          )}
          <TouchableOpacity onPress={clearFilters}>
            <Text style={styles.clearFilters}>{t('worker.clearFilters')}</Text>
          </TouchableOpacity>
        </View>
      )}

      <FlatList
        data={workers}
        renderItem={renderWorker}
        keyExtractor={item => String(item.id)}
        contentContainerStyle={styles.listContent}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Icon name="account-hard-hat" size={56} color={colors.textLight} />
            <Text style={styles.emptyText}>{t('worker.noWorkers')}</Text>
            <Text style={styles.emptySubtext}>{t('worker.noWorkersHint')}</Text>
            <Button
              mode="contained"
              icon="plus"
              onPress={() => navigation.navigate('WorkerForm')}
              style={styles.emptyButton}
              buttonColor={colors.primary}>
              {t('worker.addWorker')}
            </Button>
          </View>
        }
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary]} />
        }
      />

      <FAB icon="plus" style={styles.fab} onPress={() => navigation.navigate('WorkerForm')} color={colors.white} />

      <Portal>
        <Dialog visible={filterVisible} onDismiss={() => setFilterVisible(false)} style={{borderRadius: 15}}>
          <Dialog.Title>{t('worker.filters')}</Dialog.Title>
          <Dialog.ScrollArea style={{maxHeight: 420, paddingHorizontal: 0}}>
            <ScrollView>
              <View style={styles.filterSection}>
                <Text variant="labelLarge" style={styles.filterLabel}>{t('worker.workerType')}</Text>
                <View style={styles.filterListBox}>
                  <ScrollView nestedScrollEnabled>
                    {workerTypes.filter(wt => wt.isEnabled).map(wt => {
                      const selected = filterWorkerTypeId === wt.id;
                      return (
                        <TouchableOpacity
                          key={wt.id}
                          style={[styles.filterListItem, selected && styles.filterListItemSelected]}
                          onPress={() => setFilterWorkerTypeId(selected ? null : wt.id)}
                          activeOpacity={0.7}>
                          <Text style={[styles.filterListItemText, selected && styles.filterListItemTextSelected]}>
                            {wt.displayNameOverride || wt.name}
                          </Text>
                          {selected && <Icon name="check" size={16} color={colors.primary} />}
                        </TouchableOpacity>
                      );
                    })}
                  </ScrollView>
                </View>

                <Text variant="labelLarge" style={styles.filterLabel}>{t('worker.employmentType')}</Text>
                <View style={styles.filterListBox}>
                  <ScrollView nestedScrollEnabled>
                    {EMPLOYMENT_TYPES.map(et => {
                      const selected = filterEmploymentType === et;
                      return (
                        <TouchableOpacity
                          key={et}
                          style={[styles.filterListItem, selected && styles.filterListItemSelected]}
                          onPress={() => setFilterEmploymentType(selected ? null : et)}
                          activeOpacity={0.7}>
                          <Text style={[styles.filterListItemText, selected && styles.filterListItemTextSelected]}>
                            {EMPLOYMENT_LABELS[et]}
                          </Text>
                          {selected && <Icon name="check" size={16} color={colors.primary} />}
                        </TouchableOpacity>
                      );
                    })}
                  </ScrollView>
                </View>

                <Text variant="labelLarge" style={styles.filterLabel}>{t('worker.status')}</Text>
                <View style={styles.filterChipGroup}>
                  <Chip selected={filterStatus === true}
                    onPress={() => setFilterStatus(filterStatus === true ? null : true)}
                    style={styles.filterSelectChip} textStyle={{fontSize: 13}}>
                    {t('worker.active')}
                  </Chip>
                  <Chip selected={filterStatus === false}
                    onPress={() => setFilterStatus(filterStatus === false ? null : false)}
                    style={styles.filterSelectChip} textStyle={{fontSize: 13}}>
                    {t('worker.inactive')}
                  </Chip>
                </View>

                <Text variant="labelLarge" style={styles.filterLabel}>{t('worker.userLink')}</Text>
                <View style={styles.filterChipGroup}>
                  <Chip selected={filterHasUser === true}
                    onPress={() => setFilterHasUser(filterHasUser === true ? null : true)}
                    style={styles.filterSelectChip} textStyle={{fontSize: 13}}>
                    {t('worker.hasLogin')}
                  </Chip>
                  <Chip selected={filterHasUser === false}
                    onPress={() => setFilterHasUser(filterHasUser === false ? null : false)}
                    style={styles.filterSelectChip} textStyle={{fontSize: 13}}>
                    {t('worker.noLogin')}
                  </Chip>
                </View>
              </View>
            </ScrollView>
          </Dialog.ScrollArea>
          <Dialog.Actions>
            <Button onPress={clearFilters}>{t('worker.clearFilters')}</Button>
            <Button mode="contained" onPress={() => { setFilterVisible(false); handleSearchSubmit(); }}
              style={{borderRadius: 10}} buttonColor={colors.primary}>
              {t('worker.applyFilters')}
            </Button>
          </Dialog.Actions>
        </Dialog>

        <Dialog visible={deactivateVisible} onDismiss={() => setDeactivateVisible(false)} style={{borderRadius: 15}}>
          <Dialog.Title>{t('worker.deactivateTitle')}</Dialog.Title>
          <Dialog.Content>
            <Text>{t('worker.deactivateMessage', {name: actionWorker?.fullName})}</Text>
            <Text variant="bodySmall" style={styles.deactivateHint}>{t('worker.deactivateHint')}</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDeactivateVisible(false)}>{t('common.cancel')}</Button>
            <Button textColor={colors.error} onPress={handleDeactivate}>{t('worker.deactivate')}</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      <Snackbar visible={!!snackbar} onDismiss={() => setSnackbar('')} duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  searchRow: {flexDirection: 'row', alignItems: 'center', paddingHorizontal: spacing.sm, paddingTop: spacing.sm},
  searchbar: {flex: 1, backgroundColor: colors.surface, elevation: 0, height: 44},
  searchInput: {fontSize: 14, minHeight: 44},
  activeFilters: {
    flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center',
    paddingHorizontal: spacing.md, paddingTop: spacing.xs, gap: spacing.xs,
  },
  filterChip: {backgroundColor: colors.primary + '20', height: 30},
  filterChipText: {fontSize: 12},
  clearFilters: {color: colors.primary, fontSize: 12, fontWeight: '600', marginLeft: spacing.xs},
  listContent: {padding: spacing.md, paddingBottom: 80},

  // Card
  card: {
    flexDirection: 'row',
    backgroundColor: colors.surface,
    borderRadius: 10,
    marginBottom: spacing.sm,
    overflow: 'hidden',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border,
  },
  cardInactive: {opacity: 0.5},
  accentBar: {width: 4},
  cardBody: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.sm,
    paddingRight: spacing.md,
    gap: spacing.sm,
  },
  avatar: {
    width: 42,
    height: 42,
    borderRadius: 21,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarImage: {width: 42, height: 42, borderRadius: 21},
  avatarText: {fontSize: 18, fontWeight: '700'},
  cardInfo: {flex: 1},
  nameRow: {flexDirection: 'row', alignItems: 'center', gap: 4},
  workerName: {fontSize: 15, fontWeight: '600', color: colors.text, flexShrink: 1},
  statusDot: {width: 7, height: 7, borderRadius: 4},
  phone: {fontSize: 13, color: colors.textSecondary, marginTop: 1},
  metaRow: {flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 4, marginTop: 4},
  metaBadge: {
    backgroundColor: colors.primary + '12',
    paddingHorizontal: 6,
    paddingVertical: 1,
    borderRadius: 4,
  },
  metaBadgeText: {fontSize: 11, fontWeight: '600', color: colors.primary},
  workerCode: {fontSize: 11, color: colors.textSecondary, backgroundColor: colors.background, paddingHorizontal: 5, paddingVertical: 1, borderRadius: 3},
  orgRow: {flexDirection: 'row', alignItems: 'center', gap: 3, marginTop: 3},
  orgText: {fontSize: 12, color: colors.textLight},

  // Actions - horizontal, compact
  cardActions: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
  },
  actionCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.primary + '10',
    justifyContent: 'center',
    alignItems: 'center',
  },

  // Empty
  emptyContainer: {flex: 1, justifyContent: 'center', alignItems: 'center', paddingTop: spacing.xxl * 2},
  emptyText: {fontSize: 16, fontWeight: '600', color: colors.text, marginTop: spacing.md},
  emptySubtext: {color: colors.textSecondary, marginTop: spacing.xs, textAlign: 'center', paddingHorizontal: spacing.xl},
  emptyButton: {marginTop: spacing.lg, borderRadius: 8},
  fab: {position: 'absolute', right: spacing.md, bottom: spacing.md, backgroundColor: colors.primary},

  // Filter dialog
  filterSection: {paddingHorizontal: spacing.lg, paddingVertical: spacing.sm},
  filterLabel: {fontWeight: '600', color: colors.text, marginTop: spacing.md, marginBottom: spacing.xs},
  filterChipGroup: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs},
  filterSelectChip: {backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border},
  filterListBox: {
    borderWidth: 1, borderColor: colors.border, borderRadius: 8,
    backgroundColor: colors.surface, maxHeight: 75, overflow: 'hidden',
  },
  filterListItem: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingVertical: 5, paddingHorizontal: 12,
    borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.divider,
  },
  filterListItemSelected: {backgroundColor: colors.primary + '12'},
  filterListItemText: {fontSize: 14, color: colors.text},
  filterListItemTextSelected: {fontWeight: '600', color: colors.primary},
  deactivateHint: {color: colors.textSecondary, marginTop: spacing.sm, fontStyle: 'italic'},
});

export default WorkerListScreen;
